import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Modal,
  TextField,
} from "@mui/material";
import white from "../images/whitecap.png";
import purple from "../images/purplecap.jpg";
import green from "../images/greencap.jpg";
import blue from "../images/bluecap.jpg";
import axios from "axios";
import { API_URL } from "../components/Constant";
import { useNavigate } from "react-router-dom";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "8px",
  textAlign: "center",
};

const initialGifts = [
  {
    id: 1,
    name: "White Cap",
    image: white,
    description: "Cap",
    stock: { S: 3, M: 2, L: 1 },
    customizable: true,
  },
  {
    id: 2,
    name: "Green Bottle",
    image: green,
    description: "Bottle",
    stock: {},
    customizable: false,
  },
  {
    id: 3,
    name: "Purple T-Shirt",
    image: purple,
    description: "T-Shirt",
    stock: {},
    customizable: true,
  },
  {
    id: 4,
    name: "Blue Lunch Box",
    image: blue,
    description: "Lunch Box",
    stock: {},
    customizable: false,
  },
];

const ShopPage = () => {
  const [gifts, setGifts] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [customization, setCustomization] = useState("");
  const [selectedGiftId, setSelectedGiftId] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    message: "",
  });

  const [customizationModal, setCustomizationModal] = useState(false);
  const navigate = useNavigate();

  const handleSizeChange = (giftId, size) => {
    setSelectedSizes((prev) => {
      // If the clicked size is already selected, unselect it
      if (prev[giftId] === size) {
        const newSizes = { ...prev };
        delete newSizes[giftId];
        return newSizes;
      }
      // Otherwise, select the new size
      return {
        ...prev,
        [giftId]: size,
      };
    });
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/gifts`) // Replace with your backend URL
      .then((response) => setGifts(response.data))
      .catch((error) => console.error("Error fetching gifts:", error));
  }, []);

  const handleChooseGift = async (giftId) => {
    const selectedSize = selectedSizes[giftId];
    const gift = gifts.find((g) => g.gift_id === giftId);

    if (!gift) {
      setErrorModal({
        open: true,
        message: "Gift not found.",
      });
      return;
    }

    const hasSizeOptions = Object.keys(gift.stock).some((size) =>
      ["S", "M", "L"].includes(size),
    );
    if (hasSizeOptions && !selectedSize) {
      setErrorModal({
        open: true,
        message: "Please select a size before choosing the gift.",
      });
      return;
    }

    if (gift.customizable) {
      setSelectedGiftId(giftId);
      setCustomizationModal(true); // Show customization modal
      return;
    }

    await selectGift(giftId, selectedSize || "", "");
  };

  const selectGift = async (giftId, size, customization = "") => {
    try {
      const PlayerTocken = localStorage.getItem("PlayerTocken");
      const response = await axios.post(`${API_URL}/select-gift`, {
        PlayerTocken,
        giftId,
        size,
        customization,
      });

      // Check for successful response (status 200-299) AND success flag
      if (
        response.status >= 200 &&
        response.status < 300 &&
        response.data.success
      ) {
        // Update local state
        setGifts((prevGifts) =>
          prevGifts.map((gift) => {
            if (gift.gift_id === giftId) {
              const updatedStock = { ...gift.stock };
              if (size) updatedStock[size] = (updatedStock[size] || 0) - 1;
              return { ...gift, stock: updatedStock };
            }
            return gift;
          }),
        );
        setErrorModal({
          open: true,
          message: response.data.message || "Gift selected successfully!",
        });
        // Return success to potentially chain operations
        return true;
      } else {
        // Handle API success=false responses
        throw new Error(response.data.message);
      }
    } catch (error) {
      setErrorModal({
        open: true,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to select gift.",
      });
      return false;
    }
  };

  const handleCustomizationSubmit = async () => {
    if (!customization) {
      setErrorModal({
        open: true,
        message: "Please enter customization details.",
      });
      return;
    }

    const success = await selectGift(
      selectedGiftId,
      selectedSizes[selectedGiftId],
      customization,
    );
    if (success) {
      setCustomizationModal(false);
      setCustomization("");
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    if (isSuccess) {
      navigate("/");
    }
  };

  return (
    <Box
      sx={{
        padding: 4,
        background: "#fee4d2",
        minHeight: "100vh",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          textAlign: "center",
          mb: 4,
          fontWeight: "bold",
          color: "#333",
          textTransform: "uppercase",
          letterSpacing: "2px",
          fontFamily: "'Poppins', sans-serif",
          background: "linear-gradient(135deg, #ff6f61, #ff9a8b)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Choose Your Gift
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        {gifts.map((gift) => (
          <Grid item key={gift.gift_id} xs={12} sm={6} md={4} lg={3}>
            <Card
              sx={{
                textAlign: "center",
                padding: 2,
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(95, 13, 25, 0.3)",
                transition: "box-shadow 0.3s",
                background: "#fff2e9",
                position: "relative",
                minHeight: "400px", // Set a minimum height for the cards
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between", // Align content to space between
                "&:hover": {
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              <Box
                sx={{
                  borderRadius: "8px",
                  height: "200px", // Fixed height for the container
                  overflow: "hidden", // Ensure the image doesn't overflow
                  position: "relative",
                  display: "flex",
                  alignItems: "center", // Center the image vertically
                  justifyContent: "center", // Center the image horizontally
                }}
              >
                <CardMedia
                  component="img"
                  image={gift.image_url}
                  alt={gift.gift_name}
                  sx={{
                    borderRadius: "8px",
                    objectFit: "contain", // Ensure the full image is visible
                    width: "100%", // Make the image take the full width of the container
                    height: "auto", // Let the height adjust based on the image's aspect ratio
                    maxHeight: "200px", // Ensure the image doesn't exceed the container height
                    transition: "transform 0.3s ease-in-out, z-index 0.3s",
                    position: "relative", // Change to relative to center properly
                    zIndex: 1,
                    "&:hover": {
                      transform: "scale(1.1)",
                      zIndex: 2,
                    },
                  }}
                />
                {Object.values(gift.stock).every((stock) => stock === 0) && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "50px",
                      background: "rgba(0, 0, 0, 0.7)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 3,
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      Out of Stock
                    </Typography>
                  </Box>
                )}
              </Box>
              <CardContent
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      color: "#333",
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    {gift.gift_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      fontFamily: "'Poppins', sans-serif",
                      color: "#555",
                    }}
                  >
                    {gift.description}
                  </Typography>
                </Box>
                {Object.keys(gift.stock).some((key) => key !== "general") ? (
                  // Cap logic with sizes
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                        maxWidth: "400px",
                      }}
                    >
                      {["S", "M", "L"].map((size) => (
                        <FormControlLabel
                          key={size}
                          control={
                            <Checkbox
                              checked={selectedSizes[gift.gift_id] === size}
                              onChange={() =>
                                handleSizeChange(gift.gift_id, size)
                              }
                              disabled={
                                !gift.stock[size] || gift.stock[size] === 0
                              }
                            />
                          }
                          label={size}
                          sx={{
                            fontFamily: "'Poppins', sans-serif",
                            color:
                              gift.stock[size] && gift.stock[size] > 0
                                ? "#333"
                                : "#999",
                          }}
                        />
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        mt: 2,
                        fontWeight: "bold",
                        borderRadius: "20px",
                        padding: "10px 20px",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        background: "linear-gradient(135deg, #ff6f61, #ff9a8b)",
                        "&:hover": {
                          boxShadow: "0 6px 8px rgba(0, 0, 0, 0.2)",
                          background:
                            "linear-gradient(135deg, #ff9a8b, #ff6f61)",
                        },
                        fontFamily: "'Poppins', sans-serif",
                      }}
                      onClick={() => handleChooseGift(gift.gift_id)}
                      disabled={
                        Object.values(gift.stock).every(
                          (stock) => stock === 0,
                        ) || !selectedSizes[gift.gift_id]
                      }
                    >
                      Choose Gift
                    </Button>
                  </>
                ) : (
                  // Gifts without size (like mugs)
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        color: "#555",
                        fontWeight: "bold",
                        mt: 2,
                      }}
                    >
                      Quantity Available: {gift.stock.general || 0}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        mt: 2,
                        fontWeight: "bold",
                        borderRadius: "20px",
                        padding: "10px 20px",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        background: "linear-gradient(135deg, #ff6f61, #ff9a8b)",
                        "&:hover": {
                          boxShadow: "0 6px 8px rgba(0, 0, 0, 0.2)",
                          background:
                            "linear-gradient(135deg, #ff9a8b, #ff6f61)",
                        },
                        fontFamily: "'Poppins', sans-serif",
                      }}
                      onClick={() => handleChooseGift(gift.gift_id)}
                      disabled={gift.stock.general === 0}
                    >
                      Choose Gift
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "16px",
            background: "linear-gradient(145deg, #ffffff, #f5f5f5)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(10px)",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translate(-50%, -60%)" },
              to: { opacity: 1, transform: "translate(-50%, -50%)" },
            },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              mb: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, #ff6f61, #ff9a8b)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {isSuccess
              ? "Congratulations! 🎉"
              : selectedGiftId &&
                  gifts.find((g) => g.gift_id === selectedGiftId)?.customizable
                ? "Customize Your Gift ✍️"
                : "Message"}
          </Typography>

          {!isSuccess &&
          selectedGiftId &&
          gifts.find((g) => g.gift_id === selectedGiftId)?.customizable ? (
            <>
              <TextField
                fullWidth
                label="Enter Your Name"
                value={customization}
                onChange={(e) => setCustomization(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: {
                    borderRadius: "12px",
                    fontFamily: "'Poppins', sans-serif",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleCustomizationSubmit}
                sx={{
                  width: "100%",
                  fontWeight: "bold",
                  borderRadius: "12px",
                  padding: "12px 24px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  background: "linear-gradient(135deg, #ff6f61, #ff9a8b)",
                  "&:hover": {
                    boxShadow: "0 6px 8px rgba(0, 0, 0, 0.2)",
                    background: "linear-gradient(135deg, #ff9a8b, #ff6f61)",
                  },
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                Submit
              </Button>
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                textAlign: "center",
                color: "#555",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {modalMessage}
            </Typography>
          )}
        </Box>
      </Modal>
      {/* Error Modal */}
      <Modal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        aria-labelledby="error-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
            {errorModal.message.includes("success")
              ? "🎉 Success!"
              : "⚠️ Error"}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {errorModal.message}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setErrorModal({ ...errorModal, open: false });
              if (errorModal.message.includes("success")) {
                navigate("/");
              }
            }}
            sx={{
              background: "linear-gradient(135deg, #ff6f61, #ff9a8b)",
              "&:hover": {
                background: "linear-gradient(135deg, #ff9a8b, #ff6f61)",
              },
            }}
          >
            OK
          </Button>
        </Box>
      </Modal>

      {/* Customization Modal */}
      <Modal
        open={customizationModal}
        onClose={() => setCustomizationModal(false)}
        aria-labelledby="customization-modal-title"
      >
        <Box sx={{ ...modalStyle, width: 500 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
            ✏️ Customize Your Gift
          </Typography>
          <TextField
            fullWidth
            label="Enter your customization text"
            value={customization}
            onChange={(e) => setCustomization(e.target.value)}
            sx={{ mb: 3 }}
            multiline
            rows={4}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              onClick={() => setCustomizationModal(false)}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCustomizationSubmit}
              disabled={!customization}
              sx={{
                background: "linear-gradient(135deg, #ff6f61, #ff9a8b)",
                "&:hover": {
                  background: "linear-gradient(135deg, #ff9a8b, #ff6f61)",
                },
              }}
            >
              Submit Customization
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ShopPage;
